// ── Welle A3: Seite „Sicherheit & Tipps“ (PC und Handy) ──
// Oben das Persönliche (Sicherheits-Check, Lektion der Woche), dann der Ernstfall immer
// griffbereit, darunter alle Beiträge mit Themen-Filter und die Serien. Beiträge öffnen eine
// Ebene tief (BeitragLesen) mit Zurück. Lese-Status und Check gehören der Lehrkraft; die
// Redaktion sieht nur Summen.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MIcon } from '../../lib/micon';
import { useIsMobile } from '../../lib/hooks';
import { apiGet, apiPost, apiPut, zeigeApiFehler } from '../../lib/api';
import { toast } from '../../lib/toast';
import { BEITRAG_EVENT, globalNav, nimmBeitragZiel } from '../../lib/nav';
import { LdSheet } from '../../components/ui/LdSheet';
import { ladeNeu, useLader } from '../heute/widgets/lader';
import { BeitragLesen } from './BeitragLesen';
import { ueberallAbmelden, useAktionen } from './aktionen';
import { THEMA_ICON, THEMA_LABEL, VORLAGE_ICON, VORLAGE_LABEL, datumKurz, wochentag } from './typen';
import type { BeitragKurz, BeitragVoll, CheckPunkt, Thema, Uebersicht } from './typen';
import './sicherheit.css';

export const BEITRAEGE_PFAD = '/api/beitraege';
const bildPfad = (id: number) => `/api/beitraege/bilder/${id}`;

// ── Push auf diesem Gerät (der Server kennt nur Abos, nicht dieses Gerät) ──
function usePushAn() {
  const [an, setAn] = useState<boolean | null>(null);
  useEffect(() => {
    let aktiv = true;
    (async () => {
      try {
        if (typeof Notification === 'undefined' || Notification.permission !== 'granted' || !('serviceWorker' in navigator)) { if (aktiv) setAn(false); return; }
        const reg = await navigator.serviceWorker.getRegistration();
        const sub = await reg?.pushManager?.getSubscription();
        if (aktiv) setAn(!!sub);
      } catch { if (aktiv) setAn(false); }
    })();
    return () => { aktiv = false; };
  }, []);
  return an;
}

type CheckZeile = { punkt: CheckPunkt; titel: string; text: string; erledigt: boolean; am: string | null; selbst: boolean; knopf?: { label: string; onClick: () => void } };

/** Ernstfall-Symbol je Beitrag (nur Anzeige) */
const ernstIcon = (titel: string) => /konto/i.test(titel) ? 'lock_open' : /handy|laptop|gerät/i.test(titel) ? 'mobile_off'
  : /mail/i.test(titel) ? 'forward_to_inbox' : /link/i.test(titel) ? 'ads_click' : 'emergency_home';

export function SicherheitSeite() {
  const mobil = useIsMobile();
  const g = useLader<Uebersicht>(BEITRAEGE_PFAD);
  const d = g.daten;
  const pushAn = usePushAn();
  const [offen, setOffen] = useState<BeitragVoll | null>(null);
  const [filter, setFilter] = useState<Thema | 'neu' | 'alle'>('alle');
  const [checkOffen, setCheckOffen] = useState(false);
  const { ausfuehren, fensterEl } = useAktionen(() => void ladeNeu(BEITRAEGE_PFAD));

  // ── Beitrag öffnen (eine Ebene tief; Zurück-Taste schließt) ──
  const oeffne = useCallback(async (id: number, mitVerlauf = true) => {
    try {
      const b = await apiGet<BeitragVoll>(`/api/beitraege/${id}`);
      if (mitVerlauf) window.history.pushState({ ldBeitrag: id, ldScreen: 'mitteilungen' }, '', window.location.pathname);
      setOffen(b);
      window.scrollTo(0, 0);
    } catch (e) { zeigeApiFehler(e, 'Beitrag konnte nicht geöffnet werden'); }
  }, []);
  useEffect(() => {
    const z = nimmBeitragZiel();
    if (z) void oeffne(z);
    const neu = (e: Event) => { nimmBeitragZiel(); void oeffne((e as CustomEvent<number>).detail); };
    const zurueck = () => setOffen(null);
    window.addEventListener(BEITRAG_EVENT, neu);
    window.addEventListener('popstate', zurueck);
    return () => { window.removeEventListener(BEITRAG_EVENT, neu); window.removeEventListener('popstate', zurueck); };
  }, [oeffne]);
  function schliessen() {
    if (window.history.state?.ldBeitrag) window.history.back(); else setOffen(null);
  }
  async function gelesen() {
    if (!offen) return;
    try {
      await apiPost(`/api/beitraege/${offen.id}/gelesen`);
      setOffen({ ...offen, gelesen: true });
      void ladeNeu(BEITRAEGE_PFAD);
    } catch (e) { zeigeApiFehler(e, 'Speichern fehlgeschlagen'); }
  }
  async function bestaetige(punkt: CheckPunkt, an: boolean) {
    try {
      await apiPut(`/api/sicherheits-check/${punkt}`, { bestaetigt: an });
      await ladeNeu(BEITRAEGE_PFAD);
      if (an) toast('Bestätigt');
    } catch (e) { zeigeApiFehler(e, 'Speichern fehlgeschlagen'); }
  }

  // ── abgeleitete Daten ──
  const beitraege = useMemo(() => d?.beitraege ?? [], [d]);
  const ernstfaelle = beitraege.filter(b => b.vorlage === 'ernstfall').sort((x, y) => x.id - y.id); // feste Reihenfolge
  const lektion = useMemo(() => {
    const l = beitraege.filter(b => b.vorlage === 'lektion');
    return l.find(b => !b.gelesen) ?? l[0] ?? null;
  }, [beitraege]);
  const lektionSerie = lektion?.serie_id ? d?.serien.find(s => s.id === lektion.serie_id) : null;
  const liste = beitraege.filter(b => b.vorlage !== 'ernstfall')
    .filter(b => filter === 'alle' ? true : filter === 'neu' ? b.vorlage === 'neu' : b.thema === filter);
  const themen = (Object.keys(THEMA_LABEL) as Thema[]).filter(t => beitraege.some(b => b.thema === t));
  const naechste = useMemo(() => (d?.serien ?? []).filter(s => s.naechste).sort((a, b) => a.naechste!.datum.localeCompare(b.naechste!.datum))[0]?.naechste ?? null, [d]);

  const check: CheckZeile[] = d ? [
    { punkt: 'push', titel: 'Push-Benachrichtigungen an', text: pushAn ? 'Du erfährst sofort von Warnungen' : 'Auf diesem Gerät noch aus', erledigt: !!pushAn, am: null, selbst: false,
      knopf: pushAn ? undefined : { label: 'Einschalten', onClick: () => globalNav('einstellungen') } },
    { punkt: 'ernstfall', titel: 'Ernstfall-Lektion gelesen', text: 'Du weißt, was bei einem gehackten Konto zu tun ist', erledigt: !!d.check.ernstfall, am: null, selbst: false,
      knopf: d.check.ernstfall || !ernstfaelle[0] ? undefined : { label: 'Lesen', onClick: () => void oeffne(ernstfaelle[0].id) } },
    { punkt: 'angemeldet_bleiben', titel: '„Angemeldet bleiben“ nur am eigenen Gerät', text: 'Am Lehrerzimmer-PC ohne Haken anmelden', erledigt: !!d.check.angemeldet_bleiben, am: d.check.angemeldet_bleiben?.am ?? null, selbst: true },
    { punkt: 'bildschirmsperre', titel: 'Bildschirmsperre am Handy', text: 'PIN, Fingerabdruck oder Gesicht', erledigt: !!d.check.bildschirmsperre, am: d.check.bildschirmsperre?.am ?? null, selbst: true },
    { punkt: 'genehmigung', titel: 'Genehmigung der Schulleitung', text: 'für Schülerdaten auf privaten Geräten (§ 20 SchDSV)', erledigt: !!d.check.genehmigung, am: d.check.genehmigung?.am ?? null, selbst: true,
      knopf: d.check.erklaert.genehmigung ? { label: 'So geht’s', onClick: () => void oeffne(d.check.erklaert.genehmigung!) } : undefined },
    { punkt: 'datenexport', titel: 'Datenexport kennen', text: d.check.datenexport?.quelle === 'export' ? 'Du hast deine Daten heruntergeladen' : 'Alle deine Daten jederzeit herunterladen', erledigt: !!d.check.datenexport, am: d.check.datenexport?.quelle === 'selbst' ? d.check.datenexport.am : null, selbst: true,
      knopf: d.check.datenexport ? undefined : { label: 'Herunterladen', onClick: () => void ausfuehren('datenexport') } },
  ] : [];
  const erledigt = check.filter(c => c.erledigt).length;

  if (offen) {
    return (
      <div className="ld-si ld-si--lesen">
        <BeitragLesen b={offen} onZurueck={schliessen} onGelesen={gelesen} onAktion={a => void ausfuehren(a)} bildPfad={bildPfad} />
        {fensterEl}
      </div>
    );
  }

  const kopf = (
    <header className="ld-si-kopf">
      <h1>Sicherheit &amp; Tipps</h1>
      <p>{mobil ? 'Jeden Montag eine neue Lektion, meist in 2 Minuten' : 'Kurz und praktisch für den Schulalltag. Jeden Montag eine neue Lektion, meist in 2 Minuten.'}</p>
    </header>
  );
  if (!d) {
    return (
      <div className="ld-si">
        {kopf}
        {g.fehler
          ? <div className="ld-si-karte ld-si-fehlt"><MIcon name="cloud_off" size={22} /><span>Konnte nicht geladen werden.</span><button type="button" className="ld-si-knopf-leise" onClick={g.neu}>Erneut versuchen</button></div>
          : <div className="ld-si-karte"><div className="ld-skeleton" style={{ height: 160, borderRadius: 14 }} /></div>}
      </div>
    );
  }

  const ring = <Ring wert={erledigt} von={6} klein={mobil} />;
  const checkListe = (
    <ul className="ld-si-checkliste">
      {check.map(c => (
        <li key={c.punkt} className={c.erledigt ? 'ist-erledigt' : ''}>
          {c.selbst ? (
            <button type="button" role="checkbox" aria-checked={c.erledigt} className="ld-si-haken" aria-label={`${c.titel}: ${c.erledigt ? 'Bestätigung zurücknehmen' : 'selbst bestätigen'}`}
              onClick={() => void bestaetige(c.punkt, !c.erledigt)}>{c.erledigt && <MIcon name="check" size={16} />}</button>
          ) : (
            <span className={`ld-si-haken ist-auto`} role="img" aria-label={c.erledigt ? 'erledigt' : 'offen'}>{c.erledigt && <MIcon name="check" size={16} />}</span>
          )}
          <div className="ld-si-check-text">
            <b>{c.titel}</b>
            <small>{c.erledigt && c.am ? `selbst bestätigt am ${datumKurz(c.am)}` : c.text}</small>
          </div>
          {c.knopf && <button type="button" className="ld-si-knopf-tint" onClick={c.knopf.onClick}>{c.knopf.label}</button>}
        </li>
      ))}
    </ul>
  );

  const lektionKarte = lektion && (
    <section className="ld-si-lektion" aria-label="Lektion der Woche">
      <div className="ld-si-lektion__pills"><span>Lektion der Woche</span>{lektion.dauer_min && <span>{lektion.dauer_min} {lektion.dauer_min === 1 ? 'Minute' : 'Minuten'}</span>}</div>
      <h2>{lektion.titel}</h2>
      {lektion.teaser && <p>{lektion.teaser}</p>}
      {!mobil && lektion.lernziele.length > 0 && (
        <div className="ld-si-lektion__lernen"><span>Du lernst:</span>{lektion.lernziele.map(z => <b key={z}>{z}</b>)}</div>
      )}
      <div className="ld-si-lektion__fuss">
        <button type="button" className="ld-si-lektion__lesen" onClick={() => void oeffne(lektion.id)}>{lektion.gelesen ? 'Noch einmal lesen' : mobil ? 'Lesen' : 'Lektion lesen'}</button>
        {lektionSerie && (
          <span className="ld-si-lektion__serie">
            {!mobil && <span>{lektionSerie.titel}</span>}
            <span>{lektionSerie.gelesen} von {lektionSerie.gesamt}</span>
            <i className="ld-si-balken" aria-hidden="true"><i style={{ width: `${lektionSerie.gesamt ? Math.round(100 * lektionSerie.gelesen / lektionSerie.gesamt) : 0}%` }} /></i>
          </span>
        )}
      </div>
    </section>
  );

  const ernstfall = ernstfaelle.length > 0 && (
    <section className="ld-si-karte ld-si-ernstfall" aria-labelledby="si-ernst">
      <div className="ld-si-karte__kopf">
        <span className="ld-si-karte__icon ist-rot"><MIcon name="emergency_home" size={18} /></span>
        <h2 id="si-ernst">Im Ernstfall</h2>{!mobil && <small>Schritt für Schritt, ohne Fachbegriffe</small>}
      </div>
      <div className="ld-si-ernst-raster">
        {ernstfaelle.map(b => (
          <button key={b.id} type="button" className="ld-si-ernst" onClick={() => void oeffne(b.id)}>
            <span className="ld-si-ernst__icon" aria-hidden="true"><MIcon name={ernstIcon(b.titel)} size={19} /></span>
            <b>{b.titel}</b>
            {!mobil && b.teaser && <span className="ld-si-ernst__text">{b.teaser}</span>}
            {!mobil && b.schritte > 0 && <span className="ld-si-ernst__schritte">{b.schritte} Schritte</span>}
          </button>
        ))}
        {!mobil && (
          <div className="ld-si-sofort">
            <b>Sofort hilft</b>
            <p>Alle Sitzungen beenden, auch auf fremden Geräten. Danach Passwort ändern.</p>
            <button type="button" className="ld-si-knopf-rot" onClick={() => void ueberallAbmelden()}><MIcon name="logout" size={17} />Überall abmelden</button>
          </div>
        )}
      </div>
    </section>
  );

  const zeileBeitrag = (b: BeitragKurz) => (
    <li key={b.id}>
      <button type="button" className="ld-si-zeile" onClick={() => void oeffne(b.id)}>
        <span className="ld-si-zeile__icon" aria-hidden="true"><MIcon name={b.thema ? THEMA_ICON[b.thema] : VORLAGE_ICON[b.vorlage]} size={18} /></span>
        <span className="ld-si-zeile__text">
          <b>{b.vorlage === 'neu' && !/^neu/i.test(b.titel) ? `Neu in LehrerDesk: ${b.titel}` : b.titel}</b>
          <small>{mobil ? `${datumKurz(b.veroeffentlicht_am)}, ${VORLAGE_LABEL[b.vorlage]}` : b.teaser ?? VORLAGE_LABEL[b.vorlage]}</small>
        </span>
        <span className="ld-si-zeile__rechts">
          {b.archiviert ? null : !b.gelesen ? <span className="ld-si-neu">neu</span> : <span className="ld-si-gelesen-text"><MIcon name="check" size={14} />gelesen</span>}
          {!mobil && <small>{b.dauer_min ? `${b.dauer_min} Min.` : datumKurz(b.veroeffentlicht_am)}</small>}
        </span>
      </button>
    </li>
  );

  const alleBeitraege = (
    <section className="ld-si-karte ld-si-alle" aria-labelledby="si-alle">
      <div className="ld-si-karte__kopf">
        {!mobil && <span className="ld-si-karte__icon"><MIcon name="article" size={18} /></span>}
        <h2 id="si-alle">{mobil ? 'Beiträge' : 'Alle Beiträge'}</h2>
        {mobil && d.ungelesen > 0 && <small>{d.ungelesen} neu</small>}
      </div>
      {!mobil && (
        <div className="ld-si-filter" role="radiogroup" aria-label="Thema">
          {(['alle', ...themen, ...(beitraege.some(b => b.vorlage === 'neu') ? ['neu'] : [])] as (Thema | 'alle' | 'neu')[]).map(t => (
            <button key={t} type="button" role="radio" aria-checked={filter === t} className={filter === t ? 'ist-an' : ''} onClick={() => setFilter(t)}>
              {t === 'alle' ? 'Alle' : t === 'neu' ? 'Neu in LehrerDesk' : THEMA_LABEL[t]}
            </button>
          ))}
        </div>
      )}
      {liste.length === 0
        ? <p className="ld-si-leise">{beitraege.length ? 'Zu diesem Thema gibt es noch keinen Beitrag.' : 'Noch keine Beiträge. Neue Lektionen erscheinen hier.'}</p>
        : <ul className="ld-si-zeilen">{liste.map(zeileBeitrag)}</ul>}
    </section>
  );

  const serien = d.serien.length > 0 && (
    <section className="ld-si-karte ld-si-serien" aria-labelledby="si-serien">
      <div className="ld-si-karte__kopf"><span className="ld-si-karte__icon"><MIcon name="layers" size={18} /></span><h2 id="si-serien">Serien</h2></div>
      <ul>
        {d.serien.map(s => {
          const fertig = s.gesamt > 0 && s.gelesen === s.gesamt && !s.naechste;
          return (
            <li key={s.id} className={fertig ? 'ist-fertig' : ''}>
              <div><b>{s.titel}</b><span>{fertig ? 'fertig' : `${s.gelesen} von ${s.gesamt}`}</span></div>
              {s.beschreibung && <small>{s.beschreibung}</small>}
              <i className="ld-si-balken" aria-hidden="true"><i style={{ width: `${s.gesamt ? Math.round(100 * s.gelesen / s.gesamt) : 0}%` }} /></i>
            </li>
          );
        })}
      </ul>
      {naechste && (
        <div className="ld-si-naechste"><small>Nächste Lektion, {wochentag(naechste.datum)} {datumKurz(naechste.datum)}</small><b>{naechste.titel}</b></div>
      )}
    </section>
  );

  const fussSatz = <p className="ld-si-privat"><MIcon name="lock" size={16} />Dein Fortschritt bleibt bei dir. LehrerDesk sieht nur, wie viele eine Lektion gelesen haben, nicht wer.</p>;

  if (mobil) {
    return (
      <div className="ld-si">
        {kopf}
        <button type="button" className="ld-si-karte ld-si-check-zeile" onClick={() => setCheckOffen(true)}>
          {ring}
          <span><b>Dein Sicherheits-Check</b><small>{6 - erledigt === 0 ? 'Alles erledigt' : `${6 - erledigt} ${6 - erledigt === 1 ? 'Punkt' : 'Punkte'} noch offen`}</small></span>
          <MIcon name="chevron_right" size={22} />
        </button>
        {lektionKarte}
        {ernstfall}
        {alleBeitraege}
        {serien}
        {fussSatz}
        <LdSheet open={checkOffen} onClose={() => setCheckOffen(false)} maxWidth={560} kopf={{ titel: 'Dein Sicherheits-Check', kontext: `${erledigt} von 6 erledigt. Schützt dein Konto und die Daten deiner Klasse.`, icon: 'shield' }}>
          {checkListe}
        </LdSheet>
        {fensterEl}
      </div>
    );
  }

  return (
    <div className="ld-si">
      {kopf}
      <div className={`ld-si-oben${lektionKarte ? '' : ' ohne-lektion'}`}>
        <section className="ld-si-karte ld-si-check" aria-labelledby="si-check">
          <div className="ld-si-karte__kopf"><span className="ld-si-karte__icon"><MIcon name="shield" size={18} /></span><h2 id="si-check">Dein Sicherheits-Check</h2><small>schützt dein Konto und die Daten deiner Klasse</small></div>
          <div className="ld-si-check__inhalt">{ring}{checkListe}</div>
        </section>
        {lektionKarte}
      </div>
      {ernstfall}
      <div className="ld-si-unten">
        {alleBeitraege}
        {serien}
      </div>
      {fussSatz}
      {fensterEl}
    </div>
  );
}

function Ring({ wert, von, klein }: { wert: number; von: number; klein?: boolean }) {
  const r = klein ? 22 : 64, s = klein ? 6 : 14, gr = (r + s) * 2;
  const u = 2 * Math.PI * r;
  return (
    <span className={`ld-si-ring${klein ? ' ist-klein' : ''}`} role="img" aria-label={`${wert} von ${von} erledigt`}>
      <svg width={gr} height={gr} viewBox={`0 0 ${gr} ${gr}`} aria-hidden="true">
        <circle cx={gr / 2} cy={gr / 2} r={r} fill="none" stroke="var(--ld-si-tint-stark)" strokeWidth={s} />
        <circle cx={gr / 2} cy={gr / 2} r={r} fill="none" stroke="var(--ld-primary)" strokeWidth={s} strokeLinecap="round"
          strokeDasharray={`${u * wert / von} ${u}`} transform={`rotate(-90 ${gr / 2} ${gr / 2})`} />
      </svg>
      <span className="ld-si-ring__text">{klein ? <b>{wert}/{von}</b> : <><b>{wert} von {von}</b><small>erledigt</small></>}</span>
    </span>
  );
}
